import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomersQuery, type CustomerRow } from '@/features/customers';
import { getUiErrorMessage } from '@/shared/api';
import { Button, InlineAlert, Modal, ModalActions, Spinner, StatusPill } from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';

import { phoneDigitsForSearch, resolveOrderImageSrc } from '../lib/mapRemoteOrderToDetail';

function pickCustomerProfile(
  items: CustomerRow[],
  phoneSearch: string,
): CustomerRow | null {
  if (items.length === 0) return null;
  const normalized = phoneSearch.trim();
  if (!normalized) return items[0] ?? null;
  const exact = items.find(
    (row) => phoneDigitsForSearch(row.phoneNumber) === normalized,
  );
  return exact ?? items[0] ?? null;
}

export interface OrderCustomerProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneSearch: string;
  fallbackName: string;
}

export function OrderCustomerProfileDialog({
  open,
  onOpenChange,
  phoneSearch,
  fallbackName,
}: OrderCustomerProfileDialogProps) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const { t: tCustomers } = useTranslation('customers');

  const search = phoneSearch.trim();
  const query = useCustomersQuery(
    { page: 1, limit: 20, search },
    { enabled: open && search.length > 0 },
  );

  const profile = useMemo(
    () => (query.data ? pickCustomerProfile(query.data.items, search) : null),
    [query.data, search],
  );

  const avatarSrc = profile?.profilePicture
    ? resolveOrderImageSrc(profile.profilePicture)
    : '';

  const close = () => onOpenChange(false);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={t('detail.customer.profileModal.title')}
      description={
        search.length > 0
          ? t('detail.customer.profileModal.description', { search })
          : t('detail.customer.profileModal.noPhoneDescription')
      }
      showClose
      size="md"
      footer={
        <ModalActions>
          <Button type="button" variant="secondary" onClick={close}>
            {tc('actions.close')}
          </Button>
        </ModalActions>
      }
    >
      {search.length === 0 ? (
        <InlineAlert>{t('detail.customer.profileModal.noPhone')}</InlineAlert>
      ) : query.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="text-primary" size={28} label={tc('state.loading')} />
        </div>
      ) : query.isError ? (
        <InlineAlert>
          {getUiErrorMessage(tc, query.error) || t('detail.customer.profileModal.loadFailed')}
        </InlineAlert>
      ) : !profile ? (
        <InlineAlert>{t('detail.customer.profileModal.notFound')}</InlineAlert>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="h-14 w-14 shrink-0 rounded-full object-cover ring-4 ring-surface-muted"
              />
            ) : (
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-surface-muted text-lg font-bold text-primary ring-4 ring-surface-muted"
                aria-hidden
              >
                {(profile.fullName || fallbackName).slice(0, 2).toUpperCase() || '—'}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-lg font-bold text-fg">
                {profile.fullName || fallbackName || '—'}
              </div>
              {profile.status ? (
                <StatusPill tone="neutral" className="mt-2 text-[10px] uppercase">
                  {profile.status}
                </StatusPill>
              ) : null}
            </div>
          </div>

          <dl className="grid gap-3 text-sm">
            <ProfileRow
              label={tCustomers('table.displayId')}
              value={profile.displayId || profile.referralCode || '—'}
              mono
            />
            <ProfileRow label={tCustomers('table.phone')} value={profile.phoneNumber || '—'} />
            <ProfileRow label={tCustomers('table.email')} value={profile.email || '—'} />
            {profile.gender ? (
              <ProfileRow label={t('detail.customer.profileModal.gender')} value={profile.gender} />
            ) : null}
            <ProfileRow
              label={tCustomers('table.created')}
              value={profile.createdAt ? formatDate(profile.createdAt, 'dateTime') : '—'}
            />
            {profile.updatedAt ? (
              <ProfileRow
                label={t('detail.customer.profileModal.updated')}
                value={formatDate(profile.updatedAt, 'dateTime')}
              />
            ) : null}
          </dl>
        </div>
      )}
    </Modal>
  );
}

function ProfileRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{label}</dt>
      <dd
        className={
          mono
            ? 'font-mono text-xs font-semibold text-fg'
            : 'text-sm font-medium text-fg sm:text-end'
        }
      >
        {value}
      </dd>
    </div>
  );
}
